import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, database, createRealtimeListener, sharingAPI } from '../firebase';
import { ref, set, push, get } from 'firebase/database';
import { Settings as SettingsIcon, ChevronRight, Plus, Sparkles, Upload, Brain, Activity, Database, MessageCircle, Target, CheckCircle, Triangle, X, Check, Users, Share2, LayoutGrid } from 'lucide-react';
import Globe3D from './Globe3D';
import ProjectDetailPage from './ProjectDetailPage';
import ChatGPTImport from './ChatGPTImport';
import GlobalAIAgent from './GlobalAIAgent';
import ShareProjectModal from './ShareProjectModal';
import './DeltaTrianglePage.css';

const DeltaTrianglePage = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Auth
  const [userId, setUserId] = useState(null);
  const [userEmail, setUserEmail] = useState(null);

  // Theme
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'blue');

  // Onboarding flow
  // 0: intro, 1: name, 2: who become, 3: whats stopping, 4: projects, 5: complete
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [userName, setUserName] = useState('');
  const [userNameInput, setUserNameInput] = useState('');
  const [vision, setVision] = useState(''); // who do you want to become
  const [visionInput, setVisionInput] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [_obstacles, setObstacles] = useState(''); // what's stopping you
  const [obstaclesInput, setObstaclesInput] = useState('');
  const [projectInputs, setProjectInputs] = useState(['', '', '', '']);

  // Projects data
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  // Data Banks (Memory Highway)
  const [personalMemoryBank, setPersonalMemoryBank] = useState([]);
  const [entityBanks, setEntityBanks] = useState({}); // { projectId: [...] }
  const [_conversationLog, setConversationLog] = useState([]); // eslint-disable-line no-unused-vars
  const [dataBankStats, setDataBankStats] = useState({
    totalEvents: 0,
    recentActivity: [],
    topInsights: []
  });

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null); // { x, y, project }
  const [subProjectInput, setSubProjectInput] = useState('');
  const [showSubProjectInput, setShowSubProjectInput] = useState(false);

  // UI state
  const [introPlaying, setIntroPlaying] = useState(true);
  const [summaryActive, setSummaryActive] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [_deltaAIOpen, _setDeltaAIOpen] = useState(false); // eslint-disable-line no-unused-vars
  const [chatgptImportOpen, setChatgptImportOpen] = useState(false);

  // Sharing
  const [sharedProjects, setSharedProjects] = useState([]);
  const [shareModalProject, setShareModalProject] = useState(null);

  // 2026 Resolutions
  const [resolutionsOpen, setResolutionsOpen] = useState(false);
  const [resolutions, setResolutions] = useState([]);
  const [newResolution, setNewResolution] = useState('');

  // Apply theme to document and save to Firebase
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
    if (userId) {
      set(ref(database, `users/${userId}/profile/theme`), theme);
    }
  }, [theme, userId]);

  // Auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        setUserId(u.uid);
        setUserEmail(u.email);
        // Save email to profile for sharing lookup
        if (u.email) {
          sharingAPI.saveUserEmail(u.uid, u.email);
        }
      } else {
        setUserId(null);
        setUserEmail(null);
      }
    });
    return () => unsub();
  }, []);

  // Load saved data
  useEffect(() => {
    if (!userId) return;

    let loadedProfile = { name: null, vision: null, obstacles: null };
    let loadedProjects = [];

    const checkAndSetStep = () => {
      if (loadedProjects.length > 0 && loadedProfile.name) {
        setOnboardingStep(5); // Complete
      }
    };

    const offProjects = createRealtimeListener(`users/${userId}/projects`, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const parsed = Object.keys(data)
          .map((id) => {
            const proj = data[id];
            return {
              id,
              name: proj.name || null,
              image: proj.image || null,
              parentId: proj.parentId || null,
              goals: Array.isArray(proj.goals) ? proj.goals : [],
              todos: Array.isArray(proj.todos) ? proj.todos : [],
            };
          })
          .filter(p => p.name);
        setProjects(parsed);
        loadedProjects = parsed;
      } else {
        setProjects([]);
        loadedProjects = [];
      }
      checkAndSetStep();
    });

    const offProfile = createRealtimeListener(`users/${userId}/profile`, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        if (data.name) {
          setUserName(data.name);
          loadedProfile.name = data.name;
        }
        if (data.vision) {
          setVision(data.vision);
          loadedProfile.vision = data.vision;
        }
        if (data.obstacles) {
          setObstacles(data.obstacles);
          loadedProfile.obstacles = data.obstacles;
        }
        if (data.theme) {
          setTheme(data.theme);
        }
      }
      checkAndSetStep();
    });

    // Listen for shared projects
    const offShared = sharingAPI.createSharedProjectsListener(userId, (shared) => {
      setSharedProjects(shared);
    });

    return () => {
      try { offProjects && offProjects(); } catch {}
      try { offProfile && offProfile(); } catch {}
      try { offShared && offShared(); } catch {}
    };
  }, [userId]);

  // Combine owned and shared projects
  const allProjects = [...projects, ...sharedProjects];

  // Load Data Banks (Memory Highway)
  useEffect(() => {
    if (!userId || projects.length === 0) return;

    const loadDataBanks = async () => {
      try {
        // Load Personal Memory Bank (PMB)
        const pmbRef = ref(database, `users/${userId}/personalProfileBank`);
        const pmbSnap = await get(pmbRef);
        const pmb = pmbSnap.val() || [];
        setPersonalMemoryBank(Array.isArray(pmb) ? pmb : []);

        // Load Conversation Log (CL)
        const clRef = ref(database, `users/${userId}/conversationLog`);
        const clSnap = await get(clRef);
        const cl = clSnap.val() || [];
        setConversationLog(Array.isArray(cl) ? cl : []);

        // Load Entity Banks (ECB) for each project
        const banks = {};
        let totalEvents = 0;
        const allActivity = [];

        for (const project of projects) {
          const ecbRef = ref(database, `users/${userId}/projects/${project.id}/dataBank`);
          const ecbSnap = await get(ecbRef);
          const ecb = ecbSnap.val() || [];
          banks[project.id] = Array.isArray(ecb) ? ecb : [];
          totalEvents += banks[project.id].length;

          // Collect recent activity
          banks[project.id].forEach(event => {
            allActivity.push({
              ...event,
              projectId: project.id,
              projectName: project.name
            });
          });
        }

        setEntityBanks(banks);

        // Sort by timestamp and get recent activity
        const sortedActivity = allActivity
          .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
          .slice(0, 10);

        // Generate insights
        const completedGoals = allActivity.filter(e => e.type === 'GOAL_COMPLETED' || e.type === 'OBJECTIVE_COMPLETED');
        const completedTasks = allActivity.filter(e => e.type === 'TASK_COMPLETED');
        const addedGoals = allActivity.filter(e => e.type === 'GOAL_ADDED' || e.type === 'OBJECTIVE_CREATED');

        setDataBankStats({
          totalEvents,
          recentActivity: sortedActivity,
          topInsights: [
            { label: 'Goals Completed', value: completedGoals.length, icon: 'target' },
            { label: 'Tasks Done', value: completedTasks.length, icon: 'check' },
            { label: 'Goals Added', value: addedGoals.length, icon: 'plus' },
            { label: 'Conversations', value: cl.length, icon: 'message' }
          ]
        });

      } catch (e) {
        console.error('Failed to load data banks:', e);
      }
    };

    loadDataBanks();

    // Also set up realtime listeners for PMB and CL
    const offPMB = createRealtimeListener(`users/${userId}/personalProfileBank`, (snapshot) => {
      const data = snapshot.val();
      setPersonalMemoryBank(Array.isArray(data) ? data : []);
    });

    const offCL = createRealtimeListener(`users/${userId}/conversationLog`, (snapshot) => {
      const data = snapshot.val();
      setConversationLog(Array.isArray(data) ? data : []);
    });

    return () => {
      try { offPMB && offPMB(); } catch {}
      try { offCL && offCL(); } catch {}
    };
  }, [userId, projects]);

  // Ensure "Home" project exists
  useEffect(() => {
    if (!userId || projects.length === 0) return;

    const homeExists = projects.some(p => p.name.toLowerCase() === 'home');
    if (!homeExists) {
      const createHome = async () => {
        try {
          const projectsRef = ref(database, `users/${userId}/projects`);
          const newRef = push(projectsRef);
          await set(newRef, {
            name: 'Home',
            goals: [],
            todos: [],
            createdAt: Date.now(),
          });
        } catch (e) {
          console.error('Failed to create Home project:', e);
        }
      };
      createHome();
    }
  }, [userId, projects]);

  // Load 2026 Resolutions
  useEffect(() => {
    if (!userId) return;

    const offResolutions = createRealtimeListener(`users/${userId}/resolutions2026`, (snapshot) => {
      const data = snapshot.val();
      if (data && Array.isArray(data)) {
        setResolutions(data);
      } else {
        setResolutions([]);
      }
    });

    return () => {
      try { offResolutions && offResolutions(); } catch {}
    };
  }, [userId]);

  // Intro animation timer
  useEffect(() => {
    const introTimer = setTimeout(() => {
      setIntroPlaying(false);
    }, 1800);
    return () => clearTimeout(introTimer);
  }, []);

  // Container resize
  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        setContainerSize({ width: clientWidth, height: clientHeight });
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Spacebar handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        const tag = (e.target.tagName || '').toUpperCase();
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        e.preventDefault();

        if (onboardingStep === 0) {
          setOnboardingStep(1); // Start onboarding
        } else if (onboardingStep === 5) {
          setSummaryActive(prev => !prev); // Toggle projects view
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [onboardingStep]);

  // Save name (step 1 -> 2)
  const saveName = async () => {
    if (!userNameInput.trim() || !userId) return;
    try {
      await set(ref(database, `users/${userId}/profile/name`), userNameInput.trim());
      setUserName(userNameInput.trim());
      setOnboardingStep(2);
    } catch (e) {
      console.error('Failed to save name:', e);
    }
  };

  // Save vision (step 2 -> 3)
  const saveVision = async () => {
    if (!visionInput.trim() || !userId) return;
    try {
      await set(ref(database, `users/${userId}/profile/vision`), visionInput.trim());
      setVision(visionInput.trim());
      setOnboardingStep(3);
    } catch (e) {
      console.error('Failed to save vision:', e);
    }
  };

  // Save obstacles (step 3 -> 4)
  const saveObstacles = async () => {
    if (!obstaclesInput.trim() || !userId) return;
    try {
      await set(ref(database, `users/${userId}/profile/obstacles`), obstaclesInput.trim());
      setObstacles(obstaclesInput.trim());
      setOnboardingStep(4);
    } catch (e) {
      console.error('Failed to save obstacles:', e);
    }
  };

  // Save projects (step 4 -> 5)
  const saveProjects = async () => {
    const validProjects = projectInputs.filter(p => p.trim());
    if (validProjects.length === 0 || !userId) return;

    try {
      const projectsRef = ref(database, `users/${userId}/projects`);

      for (const name of validProjects) {
        const newRef = push(projectsRef);
        await set(newRef, {
          name: name.trim(),
          goals: [],
          todos: [],
          createdAt: Date.now(),
        });
      }
      setOnboardingStep(5);
    } catch (e) {
      console.error('Failed to save projects:', e);
    }
  };

  // Create sub-project
  const createSubProject = async () => {
    if (!subProjectInput.trim() || !contextMenu?.project || !userId) return;

    try {
      const projectsRef = ref(database, `users/${userId}/projects`);
      const newRef = push(projectsRef);
      await set(newRef, {
        name: subProjectInput.trim(),
        parentId: contextMenu.project.id,
        goals: [],
        todos: [],
        createdAt: Date.now(),
      });
      setSubProjectInput('');
      setShowSubProjectInput(false);
      setContextMenu(null);
    } catch (e) {
      console.error('Failed to create sub-project:', e);
    }
  };

  // Create new project
  // eslint-disable-next-line no-unused-vars
  const addProject = async (projectName) => {
    if (!projectName?.trim() || !userId) return;

    try {
      const projectsRef = ref(database, `users/${userId}/projects`);
      const newRef = push(projectsRef);
      await set(newRef, {
        name: projectName.trim(),
        goals: [],
        todos: [],
        createdAt: Date.now(),
      });
    } catch (e) {
      console.error('Failed to create project:', e);
    }
  };

  // Add new resolution
  const addResolution = async () => {
    if (!newResolution.trim() || !userId) return;

    try {
      const updated = [...resolutions, { text: newResolution.trim(), completed: false, createdAt: Date.now() }];
      await set(ref(database, `users/${userId}/resolutions2026`), updated);
      setNewResolution('');
    } catch (e) {
      console.error('Failed to add resolution:', e);
    }
  };

  // Toggle resolution completion
  const toggleResolution = async (index) => {
    if (!userId) return;

    try {
      const updated = resolutions.map((r, i) =>
        i === index ? { ...r, completed: !r.completed } : r
      );
      await set(ref(database, `users/${userId}/resolutions2026`), updated);
    } catch (e) {
      console.error('Failed to toggle resolution:', e);
    }
  };

  // Delete resolution
  const deleteResolution = async (index) => {
    if (!userId) return;

    try {
      const updated = resolutions.filter((_, i) => i !== index);
      await set(ref(database, `users/${userId}/resolutions2026`), updated);
    } catch (e) {
      console.error('Failed to delete resolution:', e);
    }
  };

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = () => {
      if (contextMenu && !showSubProjectInput) {
        setContextMenu(null);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [contextMenu, showSubProjectInput]);

  const { width, height } = containerSize;
  const cx = width / 2;
  const cy = height / 2;

  // Format timestamp for display
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  // Get project's data bank count
  // eslint-disable-next-line no-unused-vars
  const getProjectDataCount = (projectId) => {
    return entityBanks[projectId]?.length || 0;
  };

  // Calculate spoke positions for 4 projects
  const getProjectPosition = (index, total = 4) => {
    const startAngle = -90; // Start at top
    const angle = startAngle + (360 / total) * index;
    const rad = (angle * Math.PI) / 180;
    const radius = Math.min(width, height) * 0.35;
    return {
      x: cx + radius * Math.cos(rad),
      y: cy + radius * Math.sin(rad),
      angle,
    };
  };

  // If a project is selected, show the ProjectDetailPage
  // But ALWAYS render GlobalAIAgent on top
  if (selectedProject) {
    return (
      <>
        <ProjectDetailPage
          project={selectedProject}
          userId={userId}
          onBack={() => setSelectedProject(null)}
        />
        {/* Global AI Agent - ALWAYS ON TOP regardless of page */}
        {userId && onboardingStep === 5 && (
          <GlobalAIAgent
            userId={userId}
            projects={projects}
            currentView="project"
            currentProject={selectedProject}
            onNavigate={(type, project) => {
              if (type === 'project' && project) {
                setSelectedProject(project);
              } else if (type === 'home') {
                setSelectedProject(null);
              }
            }}
            onProjectUpdate={() => {
              console.log('Project updated by AI agent');
            }}
          />
        )}
      </>
    );
  }

  return (
    <div className="delta-triangle-page">
      <div className={`triangle-container ${summaryActive ? 'summary-active' : ''}`} ref={containerRef}>

        {/* Intro Triangle Animation */}
        <div className={`delta-triangle ${introPlaying ? 'intro-playing' : ''}`}>
          <div className="triangle-shape">
            <svg width="200" height="200" viewBox="0 0 200 200" aria-hidden="true">
              <defs>
                <linearGradient id="triangleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#4A90E2" />
                  <stop offset="50%" stopColor="#357ABD" />
                  <stop offset="100%" stopColor="#2E5B9A" />
                </linearGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              <polygon
                points="100,20 170,160 30,160"
                fill="url(#triangleGradient)"
                stroke="#4A90E2"
                strokeWidth="2"
                filter="url(#glow)"
              />
            </svg>
          </div>
          <div className="triangle-pulse-rings">
            <div className="pulse-ring"></div>
            <div className="pulse-ring"></div>
            <div className="pulse-ring"></div>
          </div>
        </div>

        {/* Onboarding Step 0: Initial prompt */}
        {onboardingStep === 0 && !introPlaying && (
          <div className="voice-status">
            <p className="status-text inspiration">Begin now.</p>
            <div className="onboarding-theme-selector">
              <span className="theme-label">Choose your color</span>
              <div className="theme-options">
                {[
                  { id: 'blue', color: '#4a90e2' },
                  { id: 'pink', color: '#e84393' },
                  { id: 'purple', color: '#7c4dff' },
                  { id: 'green', color: '#34c759' },
                  { id: 'orange', color: '#ff9500' }
                ].map((t) => (
                  <button
                    key={t.id}
                    className={`theme-option ${theme === t.id ? 'active' : ''}`}
                    style={{ background: t.color }}
                    onClick={(e) => { e.stopPropagation(); setTheme(t.id); }}
                    title={t.id.charAt(0).toUpperCase() + t.id.slice(1)}
                  />
                ))}
              </div>
            </div>
            <p className="status-hint">Press spacebar to start</p>
          </div>
        )}

        {/* Onboarding Step 1: What's your name? */}
        {onboardingStep === 1 && (
          <div className="onboarding-modal">
            <div className="onboarding-content">
              <h2 className="onboarding-question">What's your name?</h2>
              <input
                type="text"
                className="onboarding-input"
                placeholder="Enter your name..."
                value={userNameInput}
                onChange={(e) => setUserNameInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveName(); }}
                autoFocus
              />
              <button className="onboarding-btn" onClick={saveName}>
                Continue <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Onboarding Step 2: Who do you want to become? */}
        {onboardingStep === 2 && (
          <div className="onboarding-modal">
            <div className="onboarding-content">
              <h2 className="onboarding-question">Who do you want to become?</h2>
              <p className="onboarding-subtitle">Describe the person you're working to become</p>
              <input
                type="text"
                className="onboarding-input"
                placeholder="e.g., A successful entrepreneur, A world-class engineer..."
                value={visionInput}
                onChange={(e) => setVisionInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveVision(); }}
                autoFocus
              />
              <button className="onboarding-btn" onClick={saveVision}>
                Continue <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Onboarding Step 3: What's stopping you? */}
        {onboardingStep === 3 && (
          <div className="onboarding-modal">
            <div className="onboarding-content">
              <h2 className="onboarding-question">What's stopping you?</h2>
              <p className="onboarding-subtitle">Identify the obstacles in your way</p>
              <input
                type="text"
                className="onboarding-input"
                placeholder="e.g., Lack of focus, Fear of failure, Time management..."
                value={obstaclesInput}
                onChange={(e) => setObstaclesInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveObstacles(); }}
                autoFocus
              />
              <button className="onboarding-btn" onClick={saveObstacles}>
                Continue <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Onboarding Step 4: List your projects */}
        {onboardingStep === 4 && (
          <div className="onboarding-modal">
            <div className="onboarding-content">
              <h2 className="onboarding-question">List your projects</h2>
              <p className="onboarding-subtitle">What projects will help you become <strong>{vision}</strong>?</p>
              <div className="project-inputs">
                {projectInputs.map((val, i) => (
                  <input
                    key={i}
                    type="text"
                    className="onboarding-input"
                    placeholder={`Project ${i + 1}`}
                    value={val}
                    onChange={(e) => {
                      const updated = [...projectInputs];
                      updated[i] = e.target.value;
                      setProjectInputs(updated);
                    }}
                    autoFocus={i === 0}
                  />
                ))}
              </div>
              <button className="onboarding-btn" onClick={saveProjects}>
                Let's go <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Onboarding Complete: Dashboard */}
        {onboardingStep === 5 && (
          <>
            {/* Center: Globe with Name overlay */}
            <div
              className="center-globe"
              onClick={() => setSummaryActive(prev => !prev)}
              onContextMenu={(e) => {
                e.preventDefault();
                setContextMenu({ x: e.clientX, y: e.clientY, project: null, isGlobe: true });
              }}
            >
              <Globe3D size={280} theme={theme} />
              <div className="globe-overlay">
                <p className="user-name">{new Date().getFullYear()}</p>
                <p className="user-vision">{vision}</p>
              </div>
            </div>

            {/* Data Highway Stats Panel */}
            {summaryActive && dataBankStats.totalEvents > 0 && (
              <div className="data-highway-panel">
                <div className="highway-header">
                  <Database size={14} />
                  <span>DATA HIGHWAY</span>
                </div>
                <div className="highway-stats">
                  {dataBankStats.topInsights.map((insight, i) => (
                    <div key={i} className="highway-stat">
                      {insight.icon === 'target' && <Target size={12} />}
                      {insight.icon === 'check' && <CheckCircle size={12} />}
                      {insight.icon === 'plus' && <Plus size={12} />}
                      {insight.icon === 'message' && <MessageCircle size={12} />}
                      <span className="stat-value">{insight.value}</span>
                      <span className="stat-label">{insight.label}</span>
                    </div>
                  ))}
                </div>
                {dataBankStats.recentActivity.length > 0 && (
                  <div className="highway-activity">
                    <div className="activity-header">
                      <Activity size={12} />
                      <span>Recent Activity</span>
                    </div>
                    <div className="activity-list">
                      {dataBankStats.recentActivity.slice(0, 3).map((event, i) => (
                        <div key={i} className="activity-item">
                          <span className="activity-type">{event.type?.replace(/_/g, ' ')}</span>
                          <span className="activity-project">{event.projectName}</span>
                          <span className="activity-time">{formatTimeAgo(event.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {personalMemoryBank.length > 0 && (
                  <div className="highway-memory">
                    <div className="memory-header">
                      <Brain size={12} />
                      <span>Personal Memory ({personalMemoryBank.length})</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Project spokes */}
            {summaryActive && width > 0 && height > 0 && (
              <>
                <svg className="spokes-svg" width="100%" height="100%" viewBox={`0 0 ${width} ${height}`}>
                  {projects.filter(p => !p.hidden && !p.archived).slice(0, 4).map((project, i) => {
                    const visibleCount = projects.filter(p => !p.hidden && !p.archived).length;
                    const pos = getProjectPosition(i, Math.min(visibleCount, 4));
                    return (
                      <path
                        key={project.id}
                        d={`M ${cx},${cy} L ${pos.x},${pos.y}`}
                        stroke="var(--brand-500)"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        className="spoke-line-anim"
                      />
                    );
                  })}
                </svg>

                {/* Render main projects (no parentId) - includes owned + shared, excludes hidden */}
                {allProjects.filter(p => !p.parentId && !p.hidden && !p.archived).slice(0, 4).map((project, i) => {
                  const mainProjects = allProjects.filter(p => !p.parentId && !p.hidden && !p.archived);
                  const pos = getProjectPosition(i, Math.min(mainProjects.length, 4));
                  const subProjects = allProjects.filter(p => p.parentId === project.id && !p.hidden && !p.archived);
                  const isShared = project._isShared || project.sharing?.memberCount > 0;

                  return (
                    <React.Fragment key={project.id}>
                      <div
                        className={`project-node ${isShared ? 'shared' : ''}`}
                        style={{ left: pos.x, top: pos.y }}
                        onClick={() => setSelectedProject(project)}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setContextMenu({ x: e.clientX, y: e.clientY, project });
                        }}
                      >
                        <div className={`project-badge ${project.image ? 'has-image' : ''} ${isShared ? 'shared' : ''}`}>
                          {project.image ? (
                            <img src={project.image} alt={project.name} className="project-badge-image" />
                          ) : (
                            project.name.charAt(0)
                          )}
                          {isShared && (
                            <div className="shared-indicator">
                              <Users size={10} />
                            </div>
                          )}
                        </div>
                        <div className="project-name">
                          {project.name}
                          {project._isShared && (
                            <span className="shared-label">shared</span>
                          )}
                        </div>
                      </div>

                      {/* Render sub-projects */}
                      {subProjects.map((subProject, si) => {
                        const subAngle = pos.angle + (si - (subProjects.length - 1) / 2) * 25;
                        const subRad = (subAngle * Math.PI) / 180;
                        const subRadius = Math.min(width, height) * 0.15;
                        const subX = pos.x + subRadius * Math.cos(subRad);
                        const subY = pos.y + subRadius * Math.sin(subRad);

                        return (
                          <React.Fragment key={subProject.id}>
                            <svg className="sub-spoke-svg" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                              <path
                                d={`M ${pos.x},${pos.y} L ${subX},${subY}`}
                                stroke="rgba(74, 144, 226, 0.4)"
                                strokeWidth="1"
                                strokeDasharray="4,4"
                                fill="none"
                              />
                            </svg>
                            <div
                              className="project-node sub-project"
                              style={{ left: subX, top: subY }}
                              onClick={() => setSelectedProject(subProject)}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                setContextMenu({ x: e.clientX, y: e.clientY, project: subProject });
                              }}
                            >
                              <div className={`project-badge sub-badge ${subProject.image ? 'has-image' : ''}`}>
                                {subProject.image ? (
                                  <img src={subProject.image} alt={subProject.name} className="project-badge-image" />
                                ) : (
                                  subProject.name.charAt(0)
                                )}
                              </div>
                              <div className="project-name sub-name">{subProject.name}</div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </>
            )}
          </>
        )}
      </div>

      {/* 2026 Resolutions Button */}
      {onboardingStep === 5 && (
        <button
          className="resolutions-fab"
          onClick={() => setResolutionsOpen(!resolutionsOpen)}
        >
          <Triangle size={16} fill={resolutionsOpen ? 'currentColor' : 'none'} />
        </button>
      )}

      {/* 2026 Resolutions Dropdown */}
      {resolutionsOpen && (
        <div className="resolutions-dropdown">
          <div className="resolutions-header">
            <h3>2026 Resolutions</h3>
            <button className="resolutions-close" onClick={() => setResolutionsOpen(false)}>
              <X size={16} />
            </button>
          </div>
          <div className="resolutions-content">
            <div className="resolutions-add">
              <input
                type="text"
                placeholder="Add a resolution..."
                value={newResolution}
                onChange={(e) => setNewResolution(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addResolution(); }}
              />
              <button onClick={addResolution}>
                <Plus size={16} />
              </button>
            </div>
            <ul className="resolutions-list">
              {resolutions.length === 0 ? (
                <li className="resolutions-empty">No resolutions yet. Add your first one!</li>
              ) : (
                resolutions.map((res, index) => (
                  <li key={index} className={`resolution-item ${res.completed ? 'completed' : ''}`}>
                    <button
                      className="resolution-check"
                      onClick={() => toggleResolution(index)}
                    >
                      {res.completed && <Check size={12} />}
                    </button>
                    <span className="resolution-text">{res.text}</span>
                    <button
                      className="resolution-delete"
                      onClick={() => deleteResolution(index)}
                    >
                      <X size={12} />
                    </button>
                  </li>
                ))
              )}
            </ul>
            {resolutions.length > 0 && (
              <div className="resolutions-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${(resolutions.filter(r => r.completed).length / resolutions.length) * 100}%` }}
                  />
                </div>
                <span className="progress-text">
                  {resolutions.filter(r => r.completed).length}/{resolutions.length} completed
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Board FAB */}
      <button
        className="board-fab"
        onClick={() => navigate('/board')}
        title="Open Todo Board"
      >
        <LayoutGrid size={18} />
      </button>

      {/* Settings FAB */}
      <button className="settings-fab" onClick={() => setSettingsOpen(true)}>
        <SettingsIcon size={18} />
      </button>

      {/* Settings Modal */}
      {settingsOpen && (
        <div className="settings-overlay" onClick={() => setSettingsOpen(false)}>
          <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="settings-header">
              <h3>Settings</h3>
              <button className="settings-close" onClick={() => setSettingsOpen(false)}>×</button>
            </div>
            <div className="settings-content">
              <div className="setting-row account-row">
                <div className="account-info">
                  <span className="account-label">Account</span>
                  {userId && userEmail && (
                    <div className="account-details">
                      <span className="account-email">{userEmail}</span>
                      <span className="account-password">••••••••</span>
                    </div>
                  )}
                </div>
                {userId ? (
                  <button className="auth-btn" onClick={async () => {
                    const { authAPI } = await import('../firebase');
                    const result = await authAPI.signOut();
                    if (result.success) {
                      setSettingsOpen(false);
                      navigate('/login');
                    }
                  }}>Sign Out</button>
                ) : (
                  <button className="auth-btn" onClick={() => navigate('/login')}>Sign In</button>
                )}
              </div>
              <div className="setting-row theme-row">
                <span>Theme Color</span>
                <div className="theme-options">
                  {[
                    { id: 'blue', color: '#4a90e2' },
                    { id: 'pink', color: '#e84393' },
                    { id: 'purple', color: '#7c4dff' },
                    { id: 'green', color: '#34c759' },
                    { id: 'orange', color: '#ff9500' }
                  ].map((t) => (
                    <button
                      key={t.id}
                      className={`theme-option ${theme === t.id ? 'active' : ''}`}
                      style={{ background: t.color }}
                      onClick={() => setTheme(t.id)}
                      title={t.id.charAt(0).toUpperCase() + t.id.slice(1)}
                    />
                  ))}
                </div>
              </div>
              <div className="setting-row">
                <span>Reset Onboarding</span>
                <button className="auth-btn" onClick={() => {
                  setOnboardingStep(0);
                  setUserName('');
                  setUserNameInput('');
                  setVision('');
                  setVisionInput('');
                  setObstacles('');
                  setObstaclesInput('');
                  setProjectInputs(['', '', '', '']);
                  setProjects([]);
                  setSettingsOpen(false);
                }}>Reset</button>
              </div>
              <div className="setting-row import-row">
                <div className="import-info">
                  <Brain size={18} />
                  <span>Import ChatGPT Data</span>
                </div>
                <button className="auth-btn import-btn" onClick={() => {
                  setSettingsOpen(false);
                  setChatgptImportOpen(true);
                }}>
                  <Upload size={14} />
                  Import
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ChatGPT Import Modal */}
      {chatgptImportOpen && (
        <ChatGPTImport
          userId={userId}
          projects={projects}
          onClose={() => setChatgptImportOpen(false)}
          onImportComplete={(stats) => {
            console.log('Import complete:', stats);
            setChatgptImportOpen(false);
          }}
        />
      )}

      {/* Share Project Modal */}
      {shareModalProject && (
        <ShareProjectModal
          project={shareModalProject}
          userId={userId}
          userName={userName}
          onClose={() => setShareModalProject(null)}
        />
      )}

      {/* Context Menu */}
      {contextMenu && !showSubProjectInput && (
        <div
          className="context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.isGlobe ? (
            <>
              <button
                className="context-menu-item delta-ai-item"
                onClick={() => {
                  // Open Delta AI as the main home project
                  setSelectedProject({
                    id: 'delta-ai',
                    name: 'Delta AI',
                    isDeltaAI: true,
                    goals: [],
                    todos: []
                  });
                  setContextMenu(null);
                }}
              >
                <Sparkles size={14} />
                <span>Delta</span>
              </button>
              <button
                className="context-menu-item health-item"
                onClick={() => {
                  // Open Health as a special project
                  setSelectedProject({
                    id: 'health',
                    name: 'Health',
                    isHealth: true,
                    goals: [],
                    todos: []
                  });
                  setContextMenu(null);
                }}
              >
                <Activity size={14} />
                <span>Health</span>
              </button>
            </>
          ) : (
            <>
              <button
                className="context-menu-item"
                onClick={() => setShowSubProjectInput(true)}
              >
                <Plus size={14} />
                <span>Add Sub Project</span>
              </button>
              <button
                className="context-menu-item share-item"
                onClick={() => {
                  setShareModalProject(contextMenu.project);
                  setContextMenu(null);
                }}
              >
                <Share2 size={14} />
                <span>Share Project</span>
                {contextMenu.project?.sharing?.memberCount > 0 && (
                  <span className="share-count">{contextMenu.project.sharing.memberCount}</span>
                )}
              </button>
            </>
          )}
        </div>
      )}

      {/* Sub Project Input Modal */}
      {showSubProjectInput && contextMenu && (
        <div className="sub-project-overlay" onClick={() => { setShowSubProjectInput(false); setContextMenu(null); }}>
          <div className="sub-project-modal" onClick={(e) => e.stopPropagation()}>
            <h3>New Sub Project</h3>
            <p className="sub-project-parent">Parent: {contextMenu.project.name}</p>
            <input
              type="text"
              className="sub-project-input"
              placeholder="Sub project name..."
              value={subProjectInput}
              onChange={(e) => setSubProjectInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') createSubProject(); if (e.key === 'Escape') { setShowSubProjectInput(false); setContextMenu(null); } }}
              autoFocus
            />
            <div className="sub-project-actions">
              <button className="sub-project-cancel" onClick={() => { setShowSubProjectInput(false); setContextMenu(null); }}>Cancel</button>
              <button className="sub-project-create" onClick={createSubProject}>Create</button>
            </div>
          </div>
        </div>
      )}

      {/* Global AI Agent - Persistent */}
      {userId && onboardingStep === 5 && (
        <GlobalAIAgent
          userId={userId}
          projects={projects}
          currentView={selectedProject ? 'project' : 'globe'}
          onNavigate={(type, project) => {
            if (type === 'project' && project) {
              setSelectedProject(project);
            } else if (type === 'home') {
              setSelectedProject(null);
            }
          }}
          onProjectUpdate={() => {
            // Force refresh of projects from Firebase
            // The realtime listener will handle this automatically
            console.log('Project updated by AI agent');
          }}
        />
      )}

    </div>
  );
};

export default DeltaTrianglePage;
